import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  DockviewReact,
  DockviewReadyEvent,
  IDockviewPanelProps,
  DockviewApi,
} from 'dockview';
import { MessageCircle, Upload, Send, Loader } from 'lucide-react';
import 'dockview/dist/styles/dockview.css';
import './App.css';

import { AppProvider, useAppContext } from './context/AppContext';
import { AgentProvider } from './context/AgentContext';
import { initializeUIControlAPI, cleanupUIControlAPI } from './services/UIControlAPI';
import Sidebar from './components/Sidebar';
import StreamingChatAgent from './components/StreamingChatAgent';
import MapTab from './components/tabs/MapTab';
import NetworkGraphTab from './components/tabs/NetworkGraphTab';
import NetworkViewerTab from './components/tabs/NetworkViewerTab';
import MetadataTab from './components/tabs/MetadataTab';
import ComponentMetadataTab from './components/tabs/ComponentMetadataTab';
import ComponentSpecificationsTab2 from './components/tabs/ComponentSpecificationsTab2';
import EventHistoryTab from './components/tabs/EventHistoryTab';
import DocumentsTab from './components/tabs/DocumentsTab';
import AsBuiltViewerTab from './components/tabs/AsBuiltViewerTab';
import AsBuiltListTab from './components/tabs/AsBuiltListTab';
import SitesTableTab from './components/tabs/SitesTableTab';
import ExtractionDataTab from './components/tabs/ExtractionDataTab';
import WebBrowserTab from './components/tabs/WebBrowserTab';
import JupyterNotebookTab from './components/tabs/JupyterNotebookTab';
import { SiteCommunicationsTab } from './components/tabs/SiteCommunicationsTab';
import { KnowledgeMapTab } from './components/tabs/KnowledgeMapTab';
import UserProfileTab from './components/tabs/UserProfileTab';
import AdminViewTab from './components/tabs/AdminViewTab';
import { windowManager } from './utils/windowManager';
import { renderPopoutTab } from './components/PopoutTab';
import { AppState } from './types';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import LoginPage from './features/auth/LoginPage';
import SignupPage from './features/auth/SignupPage';
import InviteHandler from './features/auth/InviteHandler';
import ForgotPasswordPage from './features/auth/ForgotPasswordPage';
import AuthGate from './features/auth/AuthGate';
import VerifyResetCodePage from './features/auth/VerifyResetCodePage';
import NewPasswordPage from './features/auth/NewPasswordPage';
import GuestGate from './features/auth/GuestGate';
import { useAppSelector, useAppDispatch } from './redux/hooks';
import { useLazyGetMeQuery } from './redux/api/authApi';
import { setProfile } from './redux/slices/authSlice';
import toast from 'react-hot-toast';

// Panel components wrapper
const MapPanel: React.FC<IDockviewPanelProps> = () => <MapTab />;
// const SLDPanel: React.FC<IDockviewPanelProps> = () => <SLDTab />; // DELETED
const NetworkPanel: React.FC<IDockviewPanelProps> = () => <NetworkGraphTab />;
const NetworkViewerPanel: React.FC<IDockviewPanelProps> = () => <NetworkViewerTab />;
const MetadataPanel: React.FC<IDockviewPanelProps> = () => <MetadataTab />;
const ComponentMetadataPanel: React.FC<IDockviewPanelProps> = () => <ComponentMetadataTab />;
const ComponentListPanel: React.FC<IDockviewPanelProps> = () => <ComponentSpecificationsTab2 />;
const EventsPanel: React.FC<IDockviewPanelProps> = () => <EventHistoryTab />;
const DocumentsPanel: React.FC<IDockviewPanelProps> = () => <DocumentsTab />;
const AsBuiltPanel: React.FC<IDockviewPanelProps> = () => <AsBuiltViewerTab />;
const AsBuiltListPanel: React.FC<IDockviewPanelProps> = (props: IDockviewPanelProps) => {
  return <AsBuiltListTab />;
};
const SitesTablePanel: React.FC<IDockviewPanelProps> = () => <SitesTableTab />;
const ExtractionDataPanel: React.FC<IDockviewPanelProps> = () => <ExtractionDataTab />;
const WebBrowserPanel: React.FC<IDockviewPanelProps> = () => <WebBrowserTab />;
const NotebookPanel: React.FC<IDockviewPanelProps> = () => <JupyterNotebookTab />;
const CommunicationsPanel: React.FC<IDockviewPanelProps> = () => <SiteCommunicationsTab />;
const KnowledgePanel: React.FC<IDockviewPanelProps> = () => <KnowledgeMapTab />;
const UserProfilePanel: React.FC<IDockviewPanelProps> = () => <UserProfileTab />;
const AdminViewPanel: React.FC<IDockviewPanelProps> = () => <AdminViewTab />;

const SupportTabContent: React.FC = () => {
  const profile = useAppSelector((state) => state.auth.profile);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultName = [profile.firstName, profile.lastName].filter(Boolean).join(' ');

  const [formData, setFormData] = useState({
    name: defaultName,
    email: profile.email ?? '',
    issueType: 'technical issue',
    message: '',
  });
  const [attachment, setAttachment] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const issueTypeOptions = [
    { value: 'technical issue', label: 'Issue' },
    { value: 'feedback', label: 'General Feedback' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'improvement', label: 'Improvement' },
    { value: 'other', label: 'Other' },
  ];

  // Get Slack webhook URL from environment variable
  const slackWebhookUrl = process.env.REACT_APP_SLACK_WEBHOOK_URL || '';

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      name: defaultName || prev.name,
      email: profile.email ?? prev.email,
    }));
  }, [defaultName, profile.email]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setAttachment(file);

    const reader = new FileReader();
    reader.onloadend = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setFormData({
      name: defaultName,
      email: profile.email ?? '',
      issueType: 'technical issue',
      message: '',
    });
    setAttachment(null);
    setPreviewUrl('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email) {
      toast.error('Please fill in your name and email');
      return;
    }

    // Validate Slack webhook URL
    if (!slackWebhookUrl) {
      toast.error('Slack webhook configuration is missing. Please check your environment variables.');
      return;
    }

    setIsSubmitting(true);
    toast.loading('Sending message...', { id: 'support-submit' });

    try {
      // Format message for Slack
      const slackMessage = `*New Support Request*\n\n` +
        `*Name:* ${formData.name}\n` +
        `*Email:* ${formData.email}\n` +
        `*Issue Type:* ${formData.issueType}\n` +
        `*Message:*\n${formData.message || 'No message provided'}`;

      // Send to Slack webhook
      const response = await fetch(slackWebhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: slackMessage,
        }),
      });

      if (response.ok) {
        toast.success('Message sent successfully!', { id: 'support-submit' });
        resetForm();
      } else {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to send message');
      }
    } catch (error: any) {
      console.error('Slack Webhook Error:', error);
      toast.error(
        error?.message || 'Failed to send message. Please try again.',
        { id: 'support-submit' }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full w-full overflow-auto bg-engramiq-dark-blue p-6">
      <div className="max-w-2xl mx-auto bg-[#1a1f3a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 p-6 bg-gradient-to-r from-[#17c480] to-[#15b375]">
          <MessageCircle className="text-white" size={28} />
          <div>
            <h1 className="text-xl font-semibold text-white">Hello, how can we help?</h1>
            <p className="text-white/80 text-sm">
              Our support team typically responds within a few minutes.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-white/90 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/90 mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/90 mb-1">Issue Type *</label>
            <div className="relative">
              <select
                value={formData.issueType}
                onChange={(e) => setFormData({ ...formData, issueType: e.target.value })}
                className="w-full appearance-none pr-10 pl-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
                required
              >
                {issueTypeOptions.map((type) => (
                  <option key={type.value} value={type.value} className="bg-[#1a1f3a]">
                    {type.label}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-white/70">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-5 w-5"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.06a.75.75 0 111.08 1.04l-4.24 4.63a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-white/90 mb-1">Message</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={5}
              className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none resize-none"
              placeholder="Describe your issue..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white/90 mb-1">Attachment (Image)</label>
            <input
              type="file"
              accept="image/*"
              id="support-attachment"
              onChange={handleFileChange}
              className="hidden"
            />
            <label
              htmlFor="support-attachment"
              className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white cursor-pointer hover:bg-white/15 transition-colors"
            >
              <Upload size={20} />
              <span>{attachment ? attachment.name : 'Upload image'}</span>
            </label>
            {previewUrl && (
              <div className="mt-2">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-36 object-cover rounded-lg border border-white/10"
                />
                <button
                  type="button"
                  onClick={() => {
                    setAttachment(null);
                    setPreviewUrl('');
                  }}
                  className="mt-2 text-xs text-red-400 hover:text-red-300"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-2.5 rounded-lg font-semibold bg-[#17c480] hover:bg-[#15b375] text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader className="animate-spin" size={20} />
                Sending...
              </>
            ) : (
              <>
                <Send size={20} />
                Send Message
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

const SupportPanel: React.FC<IDockviewPanelProps> = () => <SupportTabContent />;

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dockviewApi = useRef<DockviewApi | null>(null);
  const [tabCounter, setTabCounter] = useState(0);
  const [aiPanelWidth, setAiPanelWidth] = useState(300); // Default width in pixels
  const [isResizing, setIsResizing] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isAiPanelCollapsed, setIsAiPanelCollapsed] = useState(false);
  const { state, setActiveTab, setSelectedSite, setSelectedComponents, updateFilter } = useAppContext();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const dispatch = useAppDispatch();
  const [triggerGetMe] = useLazyGetMeQuery();
  const hasFetchedProfileOnMount = useRef(false);

  // Fetch account/profile on mount/refresh if authenticated
  useEffect(() => {
    if (isAuthenticated && !hasFetchedProfileOnMount.current) {
      hasFetchedProfileOnMount.current = true;
      const fetchProfile = async () => {
        try {
          const me = await triggerGetMe().unwrap();
          dispatch(
            setProfile({
              id: me.id ?? null,
              email: me.email ?? null,
              firstName: me.first_name ?? null,
              lastName: me.last_name ?? null,
              phone: me.phone ?? null,
              role: me.role ?? null,
              tenant: me.tenant ?? null,
              active_tenant: (me as any).active_tenant ?? null,
              tenants: (me as any).tenants ?? null,
              activeSite: me.active_site ?? null,
              sessionId: me.session_id ?? null,
              permissions: me.permissions ?? null,
              isActive: typeof me.is_active === 'boolean' ? me.is_active : null,
              is_platform_admin: typeof (me as any).is_platform_admin === 'boolean' ? (me as any).is_platform_admin : null,
            })
          );
        } catch (error) {
          console.error('Failed to fetch profile on refresh:', error);
          // If the token is invalid, the error handler in apiClient will handle logout
        }
      };
      fetchProfile();
    }
    // Reset the ref when authentication state changes (e.g., on logout/login)
    if (!isAuthenticated) {
      hasFetchedProfileOnMount.current = false;
    }
  }, [isAuthenticated, dispatch, triggerGetMe]);

  // Map tab IDs to routes
  const tabToRoute: Record<string, string> = {
    map: '/map',
    network: '/network',
    networkviewer: '/network-viewer',
    metadata: '/metadata',
    componentmetadata: '/component-metadata',
    componentlist: '/component-view',
    events: '/events',
    documents: '/documents',
    asbuilt: '/as-built-viewer',
    asbuiltlist: '/site-lists',
    browser: '/browser',
    notebook: '/notebook',
    communications: '/communications',
    knowledge: '/knowledge',
    userprofile: '/user-profile',
    support: '/help-support',
    adminview: '/admin',
  };

  // Map routes to tab IDs
  const routeToTab: Record<string, string> = Object.fromEntries(
    Object.entries(tabToRoute).map(([tab, route]) => [route, tab])
  );

  useEffect(() => {
    if (!isAuthenticated && state.activeTab) {
      setActiveTab(null);
    }
  }, [isAuthenticated, state.activeTab, setActiveTab]);

  // Sync route with active tab
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const route = routeToTab[location.pathname];
    if (route && dockviewApi.current) {
      // Check if tab already exists
      const existingPanels = Array.from(dockviewApi.current.panels.values());
      const existingTab = existingPanels.find(p => {
        const panelTabType = p.id.split('-')[0];
        return panelTabType === route;
      });
      
      if (existingTab) {
        // Tab exists, just set it as active
        setActiveTab(existingTab.id);
      } else if (!state.activeTab) {
        // Tab doesn't exist and no active tab, create it
        addTab(route, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAuthenticated]);

  // Sync tab changes to route
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    if (state.activeTab) {
      const tabId = state.activeTab.split('-')[0]; // Extract base tab ID
      const route = tabToRoute[tabId];
      if (route && location.pathname !== route) {
        navigate(route, { replace: true });
      }
    }
  }, [state.activeTab, navigate, location.pathname, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    // Set up cross-window communication
    const handlePopoutMessage = (data: any) => {
      switch (data.type) {
        case 'popout-ready':
          // Send current state to newly opened window
          const targetWindow = windowManager.getOpenWindows()
            .find(w => w.id === data.windowId)?.window;
          if (targetWindow) {
            renderPopoutTab(data.tabType, getTabTitle(data.tabType), data.windowId, targetWindow);
            windowManager.sendToWindow(data.windowId, { state });
          }
          break;
        case 'state-update':
          // Update local state based on changes from popout window
          const newState = data.state;
          if (newState.selectedSite !== state.selectedSite) {
            setSelectedSite(newState.selectedSite);
          }
          if (JSON.stringify(newState.selectedComponents) !== JSON.stringify(state.selectedComponents)) {
            setSelectedComponents(newState.selectedComponents);
          }
          if (JSON.stringify(newState.filterCriteria) !== JSON.stringify(state.filterCriteria)) {
            updateFilter(newState.filterCriteria);
          }
          break;
        case 'close-popout':
          windowManager.closeWindow(data.windowId);
          break;
      }
    };

    windowManager.onMessage(handlePopoutMessage);

    return () => {
      // No offMessage helper yet; handlers are short-lived per session.
    };
  }, [state, setSelectedSite, setSelectedComponents, updateFilter, isAuthenticated]);

  // Sync state to all popout windows when it changes
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    windowManager.sendToAllWindows({ state });
  }, [state, isAuthenticated]);

  const getTabTitle = (tabType: string): string => {
    const titles: Record<string, string> = {
      map: 'Map View',
      // sld: 'Single Line Diagram', // DELETED
      network: 'Network Graph',
      networkviewer: 'Network Viewer',
      metadata: 'Metadata',
      componentmetadata: 'Component Metadata',
      componentlist: 'Component View',
      events: 'Event History',
      documents: 'Documents',
      asbuilt: 'As-Built Viewer',
      asbuiltlist: 'As-Built List',
      sitestable: 'Sites',
      extractiondata: 'Extraction Data',
      browser: 'Web Browser',
      notebook: 'Jupyter Notebook',
      communications: 'Site Communications',
      knowledge: 'Knowledge Map',
      userprofile: 'User Profile',
      support: 'Help & Support',
      adminview: 'Admin View'
    };
    return titles[tabType] || tabType;
  };

  const onReady = (event: DockviewReadyEvent) => {
    dockviewApi.current = event.api;
    
    if (!isAuthenticated) {
      return;
    }
    // Check if we're on a specific route, otherwise default to Sites List
    const route = location.pathname;
    const tabType = routeToTab[route] || 'sitestable';
    
    // Check if panel already exists before adding
    const existingPanels = Array.from(event.api.panels.values());
    const existingTab = existingPanels.find(p => {
      const panelTabType = p.id.split('-')[0];
      return panelTabType === tabType;
    });
    
    if (!existingTab) {
      addTab(tabType);
    } else {
      // Tab exists, just set it as active
      setActiveTab(existingTab.id);
      const mappedRoute = tabToRoute[tabType];
      if (mappedRoute && location.pathname !== mappedRoute) {
        navigate(mappedRoute, { replace: true });
      }
    }
  };

  const addTab = (tabType: string, popOut: boolean = false, position?: 'right' | 'bottom' | 'left' | 'top') => {
    if (!isAuthenticated) {
      return;
    }
    if (popOut) {
      try {
        const title = getTabTitle(tabType);
        windowManager.openPopout(tabType, title);
      } catch (error) {
        console.error('Failed to open pop-out window:', error);
        // Fallback to regular tab
        addTab(tabType, false);
      }
      return;
    }

    if (!dockviewApi.current) return;

    // Check if panel with this tab type already exists
    const existingPanels = Array.from(dockviewApi.current.panels.values());
    const existingTab = existingPanels.find(p => {
      const panelTabType = p.id.split('-')[0];
      return panelTabType === tabType;
    });
    
    if (existingTab) {
      // Tab already exists, activate it in dockview and update state
      try {
        const panel = dockviewApi.current.getPanel(existingTab.id);
        if (panel && panel.api) {
          // Use dockview's API to set the panel as active
          panel.api.setActive();
        }
      } catch (error) {
        // If panel activation fails, still update the state
        console.warn('Failed to activate existing panel:', error);
      }
      setActiveTab(existingTab.id);
      const route = tabToRoute[tabType];
      if (route) {
        navigate(route, { replace: true });
      }
      return;
    }

    const tabConfigs: Record<string, { title: string; component: string }> = {
      map: { title: 'Map View', component: 'map' },
      // sld: { title: 'Single Line Diagram', component: 'sld' }, // DELETED
      network: { title: 'Network Graph', component: 'network' },
      networkviewer: { title: 'Network Viewer', component: 'networkviewer' },
      metadata: { title: 'Metadata', component: 'metadata' },
      componentmetadata: { title: 'Component Metadata', component: 'componentmetadata' },
      componentlist: { title: 'Component View', component: 'componentlist' },
      events: { title: 'Event History', component: 'events' },
      documents: { title: 'Documents', component: 'documents' },
      asbuilt: { title: 'As-Built Viewer', component: 'asbuilt' },
      asbuiltlist: { title: 'As-Built List', component: 'asbuiltlist' },
      sitestable: { title: 'Sites', component: 'sitestable' },
      extractiondata: { title: 'Extraction Data', component: 'extractiondata' },
      browser: { title: 'Web Browser', component: 'browser' },
      notebook: { title: 'Jupyter Notebook', component: 'notebook' },
      communications: { title: 'Site Communications', component: 'communications' },
      knowledge: { title: 'Knowledge Map', component: 'knowledge' },
      userprofile: { title: 'User Profile', component: 'userprofile' },
      support: { title: 'Help & Support', component: 'support' },
      adminview: { title: 'Admin View', component: 'adminview' },
    };

    const config = tabConfigs[tabType];
    if (!config) return;

    const id = `${tabType}-${tabCounter}`;
    setTabCounter(prev => prev + 1);

    // If position is specified and there's an active group, create a split view
    if (position && dockviewApi.current.activeGroup) {
      // Create a new group in the specified direction
      const newGroup = dockviewApi.current.addGroup({
        direction: position,
        referenceGroup: dockviewApi.current.activeGroup,
      });

      // Add panel to the new group
      dockviewApi.current.addPanel({
        id,
        component: config.component,
        title: config.title,
        position: { referenceGroup: newGroup },
      });
    } else {
      // Default behavior - add to current group
      dockviewApi.current.addPanel({
        id,
        component: config.component,
        title: config.title,
      });
    }

    setActiveTab(id);
    
    // Update route when tab is added
    const route = tabToRoute[tabType];
    if (route) {
      navigate(route, { replace: true });
    }
  };

  const popOutTab = (tabType: string) => {
    addTab(tabType, true);
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;
    
    const newWidth = window.innerWidth - e.clientX;
    const minWidth = 300;
    const maxWidth = 600;
    
    setAiPanelWidth(Math.min(Math.max(newWidth, minWidth), maxWidth));
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    // Only handle drags from sidebar buttons (those with our custom tab-id data)
    const hasTabId = e.dataTransfer.types.includes('application/tab-id');
    if (hasTabId) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    const tabId = e.dataTransfer.getData('application/tab-id');
    if (tabId) {
      e.preventDefault();
      e.stopPropagation();

      // Calculate drop position based on mouse location relative to viewport
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      // Determine which edge is closest (with 25% threshold zones)
      const rightThreshold = viewportWidth * 0.75;
      const bottomThreshold = viewportHeight * 0.75;
      const leftThreshold = viewportWidth * 0.25;
      const topThreshold = viewportHeight * 0.25;

      let position: 'right' | 'bottom' | 'left' | 'top' | undefined;

      if (mouseX > rightThreshold) {
        position = 'right';
      } else if (mouseX < leftThreshold) {
        position = 'left';
      } else if (mouseY > bottomThreshold) {
        position = 'bottom';
      } else if (mouseY < topThreshold) {
        position = 'top';
      }
      // If in center zone, no position specified = add as tab to current group

      addTab(tabId, false, position);
    }
    // Otherwise, let Dockview handle the drop for internal tab dragging
  }, [addTab]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Listen for custom tab open events from child components
  useEffect(() => {
    const handleOpenTab = (event: CustomEvent<{ tabType: string }>) => {
      if (event.detail?.tabType) {
        addTab(event.detail.tabType);
      }
    };

    window.addEventListener('openTab', handleOpenTab as EventListener);
    return () => {
      window.removeEventListener('openTab', handleOpenTab as EventListener);
    };
  }, [addTab]);

  return (
    <Routes>
      <Route path="/login" element={<GuestGate><LoginPage /></GuestGate>} />
      <Route path="/signup" element={<GuestGate><SignupPage /></GuestGate>} />
      <Route path="/invite" element={<InviteHandler />} />
      <Route path="/forgot-password" element={<GuestGate><ForgotPasswordPage /></GuestGate>} />
      <Route path="/reset-password/verify" element={<GuestGate><VerifyResetCodePage /></GuestGate>} />
      <Route path="/reset-password/new" element={<GuestGate><NewPasswordPage /></GuestGate>} />
      <Route
        path="/*"
        element={
          <AuthGate>
            <div className="h-screen flex flex-col bg-engramiq-dark-blue font-figtree overflow-hidden">
              <div className="flex-1 flex min-h-0">
                <Sidebar 
                  onAddTab={addTab} 
                  onPopOutTab={popOutTab} 
                  isCollapsed={isSidebarCollapsed} 
                  onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
                />
                <div className="flex-1 flex min-h-0">
                  <div className="flex-1 min-h-0">
                    <DockviewReact
                      onReady={onReady}
                      components={{
                        map: MapPanel,
                        // sld: SLDPanel, // DELETED
                        network: NetworkPanel,
                        networkviewer: NetworkViewerPanel,
                        metadata: MetadataPanel,
                        componentmetadata: ComponentMetadataPanel,
                        componentlist: ComponentListPanel,
                        events: EventsPanel,
                        documents: DocumentsPanel,
                        asbuilt: AsBuiltPanel,
                        asbuiltlist: AsBuiltListPanel,
                        sitestable: SitesTablePanel,
                        extractiondata: ExtractionDataPanel,
                        browser: WebBrowserPanel,
                        notebook: NotebookPanel,
                        communications: CommunicationsPanel,
                        knowledge: KnowledgePanel,
                        userprofile: UserProfilePanel,
                        support: SupportPanel,
                        adminview: AdminViewPanel,
                      }}
                      className="dockview-theme-dark"
                    />
                  </div>
                  {/* <div className="relative flex min-h-0">
                    <div
                      className="w-1 bg-engramiq-green/20 hover:bg-engramiq-green/40 cursor-col-resize transition-colors flex-shrink-0"
                      onMouseDown={handleMouseDown}
                    />
                    <div 
                      className="bg-engramiq-dark-blue flex-shrink-0 h-full overflow-hidden"
                      style={{ width: aiPanelWidth }}
                    >
                      <StreamingChatAgent onOpenTab={addTab} />
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          </AuthGate>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <AppProvider>
      <AgentProvider enableAutoReconnect={true}>
        <GlobalUIAPI />
        <AppContent />
      </AgentProvider>
    </AppProvider>
  );
}

// Global UI API Component - exposes React controls to backend
function GlobalUIAPI() {
  const appContext = useAppContext();
  
  React.useEffect(() => {
    // Initialize UI Control API service
    const apiService = initializeUIControlAPI();
    
    // Expose global UI API on window for backend access
    const uiAPI = {
      // UI State
      getUIState: () => ({
        url: window.location.href,
        title: document.title,
        activeTab: appContext.state.activeTab,
        selectedComponents: appContext.state.selectedComponents,
        selectedSite: appContext.state.selectedSite,
        filterCriteria: appContext.state.filterCriteria,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        timestamp: new Date().toISOString()
      }),
      
      // Navigation & Tab Control
      navigateToTab: (tabName: string) => {
        // Map tab names to IDs
        const tabMap: Record<string, string> = {
          'map': 'map-0',
          'network': 'network-0', 
          'sld': 'sld-0',
          'metadata': 'metadata-0',
          'events': 'events-0',
          'documents': 'documents-0',
          'browser': 'browser-0',
          'notebook': 'notebook-0'
        };
        const tabId = tabMap[tabName.toLowerCase()] || tabName;
        appContext.setActiveTab(tabId);
        return { success: true, message: `Navigated to ${tabName} tab` };
      },
      
      // Component Selection
      selectComponents: (componentIds: string[]) => {
        appContext.setSelectedComponents(componentIds);
        return { success: true, message: `Selected components: ${componentIds.join(', ')}` };
      },
      
      // Component Details
      getComponentDetails: (componentId: string) => {
        // This would typically fetch from your data source
        // For now, return mock data based on component ID
        return {
          id: componentId,
          type: componentId.startsWith('inv') ? 'inverter' : 
                componentId.startsWith('cb') ? 'combiner' :
                componentId.startsWith('mod') ? 'module' : 'component',
          name: `Component ${componentId}`,
          status: 'operational',
          metadata: {
            location: { x: 100, y: 200 },
            lastUpdate: new Date().toISOString()
          }
        };
      },
      
      // Filtering
      updateFilter: (filter: any) => {
        appContext.updateFilter(filter);
        return { success: true, message: 'Filter updated' };
      },
      
      // Site Selection  
      selectSite: (siteId: string) => {
        appContext.setSelectedSite(siteId);
        return { success: true, message: `Selected site: ${siteId}` };
      },
      
      // Scroll Control (simulate via JavaScript)
      scroll: (direction: string, amount: number) => {
        const scrollAmount = direction === 'up' ? -amount : amount;
        if (direction === 'down' || direction === 'up') {
          window.scrollBy(0, scrollAmount);
        } else if (direction === 'left' || direction === 'right') {
          window.scrollBy(direction === 'left' ? -amount : amount, 0);
        }
        return { success: true, message: `Scrolled ${direction} by ${amount}px` };
      },
      
      // Execute JavaScript
      executeJavaScript: (code: string) => {
        try {
          const result = eval(code);
          return { success: true, result };
        } catch (error) {
          return { success: false, error: (error as Error).message };
        }
      },
      
      // Get visible elements (simplified)
      getVisibleElements: () => {
        const elements = Array.from(document.querySelectorAll('[data-component-id], button, .clickable')).map(el => {
          const rect = el.getBoundingClientRect();
          return {
            tagName: el.tagName.toLowerCase(),
            id: el.id || null,
            className: el.className || null,
            textContent: el.textContent?.trim().substring(0, 100) || '',
            bounds: {
              x: Math.round(rect.left),
              y: Math.round(rect.top), 
              width: Math.round(rect.width),
              height: Math.round(rect.height)
            }
          };
        });
        return elements;
      }
    };
    
    // Expose API globally
    (window as any).solarFarmAPI = uiAPI;
    
    // Cleanup
    return () => {
      delete (window as any).solarFarmAPI;
      cleanupUIControlAPI();
    };
  }, [appContext]);
  
  return null; // This component doesn't render anything
}

export default App;
